import { useEffect } from "react";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";

type AnnouncerStyle = "gameshow" | "casino" | "party" | "professional";

interface VoiceAnnouncerProps {
  style?: AnnouncerStyle;
  onReady?: () => void;
}

export const useVoiceAnnouncer = ({ style = "gameshow" }: VoiceAnnouncerProps = {}) => {
  const { speak, isSpeaking, stopSpeaking } = useTextToSpeech();

  const announcements = {
    gameshow: {
      voice: "9BWtsMINqrJLrRacOk9x", // Aria - energetic female
      three: (number: number) => `And the number is… ${number}! Only three numbers remain!`,
      two: (number: number) => `And the number is… ${number}! Just two more to go!`,
      winner: (number: number) => `AND HERE IT IS!!! THE FINAL NUMBER IS… ${number}!!! CONGRATULATIONS!!!`,
    },
    casino: {
      voice: "CwhRBWXzGAHq8TQ4Fs17", // Roger - professional male (deeper voice)
      three: (number: number) => `And the number is… ${number}!`,
      two: (number: number) => `And the number is… ${number}!`,
      winner: (number: number) => `And the winning number is… ${number}!`,
    },
    party: {
      voice: "EXAVITQu4vr4xnSDxMaL", // Sarah - friendly female
      three: (number: number) => `It's ${number}! Only three more numbers to go!`,
      two: (number: number) => `Number ${number}! Just two more!`,
      winner: (number: number) => `YES YES YES! The winner is NUMBER ${number}! You did it! CONGRATULATIONS!!!`,
    },
    professional: {
      voice: "CwhRBWXzGAHq8TQ4Fs17", // Roger - professional male
      three: (number: number) => `Number ${number}. Three numbers remaining.`,
      two: (number: number) => `Number ${number}. Two numbers remain.`,
      winner: (number: number) => `The winning number is ${number}. Congratulations to the winner.`,
    },
  };

  const currentAnnouncements = announcements[style];

  const announceThree = async (number?: number) => {
    const message = number !== undefined ? currentAnnouncements.three(number) : "Three!";
    await speak(message, { voice: currentAnnouncements.voice });
  };

  const announceTwo = async (number?: number) => {
    const message = number !== undefined ? currentAnnouncements.two(number) : "Two!";
    await speak(message, { voice: currentAnnouncements.voice });
  };

  const announceWinner = async (number: number) => {
    await speak(currentAnnouncements.winner(number), { voice: currentAnnouncements.voice });
  };

  const announceCustom = async (text: string) => {
    await speak(text, { voice: currentAnnouncements.voice });
  };

  return {
    announceThree,
    announceTwo,
    announceWinner,
    announceCustom,
    isSpeaking,
    stopSpeaking,
  };
};
